
import { useState, useEffect, RefObject, useRef, useCallback } from 'react';
import { VideoFeatures } from '@/store/videoFeatures';

/**
 * Hook to manage autoplay overlay functionality
 */
export function useAutoplayOverlay(
  videoRef: RefObject<HTMLVideoElement>,
  features: VideoFeatures,
  containerRef?: RefObject<HTMLElement>
) {
  // Safely check if autoplayOverlay exists and is enabled
  const autoplayOverlayEnabled = features.autoplayOverlay?.enabled || false;

  // Track if user has clicked the overlay (user interaction happened)
  const userHasInteracted = useRef(false);
  // Track if autoplay has been initiated to avoid re-playing when template changes
  const hasInitiatedAutoplay = useRef(false);

  const [showAutoplayOverlay, setShowAutoplayOverlay] = useState(() => {
    return autoplayOverlayEnabled && !userHasInteracted.current;
  });

  // State to show play icon when video is paused (after autoplay interaction)
  const [showPlayIcon, setShowPlayIcon] = useState(false);

  // Sync overlay visibility when feature is toggled
  useEffect(() => {
    if (!autoplayOverlayEnabled) {
      if (showAutoplayOverlay) {
        setShowAutoplayOverlay(false);
      }
      hasInitiatedAutoplay.current = false;
      userHasInteracted.current = false;

      const video = videoRef.current;
      if (video && !video.paused) {
        video.pause();
      }
    }
  }, [autoplayOverlayEnabled, showAutoplayOverlay, videoRef]);

  // Handle autoplay initiation
  useEffect(() => {
    if (!autoplayOverlayEnabled || !features.autoPlay) return;
    if (hasInitiatedAutoplay.current || userHasInteracted.current) return;

    const video = videoRef.current;
    if (!video) return;

    // Set video as muted to allow autoplay
    video.muted = true;

    // Mark as initiated to prevent re-playing when template changes
    hasInitiatedAutoplay.current = true;

    // Show overlay if not already shown
    if (!showAutoplayOverlay) {
      setShowAutoplayOverlay(true);
    }

    // Start playing the video muted
    if (video.paused) {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(err => {
          // Silenciar AbortError que acontece durante mudanças de template
          if (err.name !== 'AbortError') {
            console.error('Autoplay failed:', err);
          }
        });
      }
    }
  }, [autoplayOverlayEnabled, features.autoPlay, videoRef, showAutoplayOverlay]);

  // Handle autoplay overlay click
  const handleAutoplayOverlayClick = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    // Mark that user has interacted - prevents overlay from showing again
    userHasInteracted.current = true;

    // Restart video from the beginning (only when user clicks)
    video.currentTime = 0;

    // Unmute the video and set volume
    video.muted = false;
    video.volume = 1.0; // Set volume to maximum

    // Play the video
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch(err => {
        // Silenciar AbortError
        if (err.name !== 'AbortError') {
          console.error('Play failed:', err);
        }
      });
    }

    // Hide the overlay
    setShowAutoplayOverlay(false);
  }, [videoRef]);

  // Listen for video pause/play events to show/hide play icon
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleVideoPause = () => {
      // Show play icon in two scenarios:
      // 1. Autoplay is enabled and user has already interacted (autoplay overlay was clicked)
      // 2. Autoplay is NOT enabled but controls are disabled or hidden
      const shouldShowIcon =
        (autoplayOverlayEnabled && userHasInteracted.current) ||
        (!autoplayOverlayEnabled && (features.disableControls || !features.showControls));

      if (shouldShowIcon) {
        setShowPlayIcon(true);
      }
    };

    const handleVideoPlay = () => {
      setShowPlayIcon(false);
    };

    video.addEventListener('pause', handleVideoPause);
    video.addEventListener('play', handleVideoPlay);

    return () => {
      video.removeEventListener('pause', handleVideoPause);
      video.removeEventListener('play', handleVideoPlay);
    };
  }, [autoplayOverlayEnabled, features.disableControls, features.showControls, videoRef]);

  // Handle play icon click
  const handlePlayIconClick = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    setShowPlayIcon(false);
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch(err => {
        if (err.name !== 'AbortError') {
          console.error('Play failed:', err);
        }
      });
    }
  }, [videoRef]);

  return {
    autoplayOverlayEnabled,
    showAutoplayOverlay,
    handleAutoplayOverlayClick,
    showPlayIcon,
    handlePlayIconClick
  };
}
